/**
 * 학원 리스트/상세 뷰 카운팅용 경로 판별 유틸.
 * pathname만으로 "진입" 여부를 판단할 때 사용.
 */

const ACADEMY_LIST_PATH = '/academy';

/** [id] 직하위 예약 경로 (상세 [id]/[name]이 아님) */
const ACADEMY_ID_RESERVED_SEGMENTS = [
  'review-list',
  'review-write',
  'review-report',
] as const;

/**
 * 학원 리스트 페이지 pathname 여부
 * - /academy 또는 /academy/
 */
export function isAcademyList(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === ACADEMY_LIST_PATH;
}

/**
 * 학원 상세 본문 페이지 pathname 여부
 * - /academy/[id]/[name] 형태이며, [name]이 예약 경로가 아닐 때만 true
 * - 리뷰리스트·리뷰작성·리뷰신고 등은 상세가 아님
 */
export function isAcademyDetail(pathname: string): boolean {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length !== 3 || parts[0] !== 'academy') return false;
  const segmentAfterId = parts[2];
  return !ACADEMY_ID_RESERVED_SEGMENTS.includes(
    segmentAfterId as (typeof ACADEMY_ID_RESERVED_SEGMENTS)[number],
  );
}

/**
 * 학원 상세 pathname에서 academy id 추출
 * - /academy/123/name → '123'
 */
export function getAcademyIdFromDetailPath(pathname: string): string | null {
  if (!isAcademyDetail(pathname)) return null;
  const parts = pathname.split('/').filter(Boolean);
  return parts[1] ?? null;
}

/**
 * 이전 경로가 "같은 학원의 하위 페이지"인지
 * - toDetailPath: 도착한 학원 상세 path (isAcademyDetail true)
 * - fromPath: 직전 pathname
 * - 같은 id의 리뷰리스트/가격/결제/테스트코스 등에서 돌아온 경우 true
 */
export function isSameAcademySubRoute(
  fromPath: string,
  toDetailPath: string,
): boolean {
  const academyId = getAcademyIdFromDetailPath(toDetailPath);
  if (!academyId) return false;

  const prefix = `/academy/${academyId}`;
  if (fromPath === prefix || fromPath === prefix + '/') return true;
  if (!fromPath.startsWith(prefix + '/')) return false;
  return fromPath !== toDetailPath;
}

/**
 * 학원 리스트 "진입" 카운트 여부
 * - 현재가 리스트이고, 이전 경로가 /academy/ 하위가 아닐 때 true (밖에서 들어온 경우)
 */
export function shouldCountListEntry(
  prevPath: string,
  currentPath: string,
): boolean {
  if (!isAcademyList(currentPath)) return false;
  if (!prevPath) return true;
  const normalized = prevPath.replace(/\/+$/, '') || '/';
  return normalized !== ACADEMY_LIST_PATH && !prevPath.startsWith('/academy/');
}

/**
 * 학원 상세 "진입" 카운트 여부
 * - 현재가 상세이고, 이전 경로가 같은 학원의 하위가 아닐 때 true
 */
export function shouldCountDetailEntry(
  prevPath: string,
  currentPath: string,
): boolean {
  if (!isAcademyDetail(currentPath)) return false;
  if (!prevPath) return true;
  return !isSameAcademySubRoute(prevPath, currentPath);
}
